/**
 * VoxelGrid — the minimal occupancy carrier the separation-profile landmarker
 * analyses (ADR-010 §2 Phase B, §3 "hand topology analysis"). ONE responsibility
 * here: define the grid data shape + build one from the declarative box-fixture
 * language, so a later PR can adapt the Voxel-Skinner voxelizer's output to it
 * without this module knowing anything about meshes or engines.
 *
 * A grid is plain data: integer `dims`, a positive `voxelSize` (h, the geodesic
 * distance unit), a set of occupied linear voxel indices, and a wrist-seed set
 * (the BFS distance-0 shell — the wrist cut from ADR-010 §2 ①). Linear index
 * convention is `x + nx*(y + ny*z)`, matching the fixture `wristSeed` encoding.
 *
 * Fail-loud line (ADR-010 §7, LAWS "fail loudly over silent fallback"):
 * STRUCTURAL violations of the grid contract — non-positive/non-integer dims, a
 * non-finite/non-positive voxelSize, empty occupancy, an empty seed set, or any
 * occupied/seed index that is not an in-range integer — throw
 * `InvalidVoxelGridError`. They mean the *producer* (a voxelizer/adapter) built
 * a broken grid; that is our data bug, not a product outcome. The distinct case
 * of a well-formed seed index that simply misses the volume is NOT decided here
 * — it is an honest "the wrist cut missed the hand" that the analyzer reports as
 * the named `invalid-grid` result (see separation-profile.ts), because the
 * fixtures treat it as a correctable product outcome, not a crash.
 *
 * Depends on: ir/errors (KernelError). Depended on by: hands/separation-profile,
 * hands/separation-profile.test, and (future) the Voxel-Skinner adapter PR.
 *
 * Credit: the geodesic/box-expansion machinery this feeds originates from GPT
 * 5.6 Sol's fourth hand-landmarker delivery (accepted with reservations,
 * docs/research/proposals/hand-landmarker/REVIEW-2026-07-11.md), reimplemented
 * in kernel style and integrated with the R1–R5 corrections.
 */
import { KernelError } from '../ir/errors.js';
import type { Vec3Data } from '../math/vec3.js';

/** A position in voxel-INDEX space (fractional after centroid averaging). Not a world-space vector. */
export interface VoxelPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** An inclusive integer axis-aligned box; the fixture shape primitive (treatment §7). */
export interface VoxelBox {
  readonly min: readonly [number, number, number];
  readonly max: readonly [number, number, number];
}

/**
 * The declarative fixture form the golden vectors use (treatment §7): the
 * occupied volume is the union of `shapes` (or an explicit `occupied` index
 * list), and `wristSeed` holds flattened `x + nx*(y + ny*z)` indices. `voxelSize`
 * defaults to 1. This is the shape a Voxel-Skinner adapter will eventually
 * populate — the analyzer never sees a mesh.
 */
export interface BoxFixture {
  readonly dims: readonly [number, number, number];
  readonly voxelSize?: number;
  readonly shapes?: readonly VoxelBox[];
  readonly occupied?: readonly number[];
  readonly wristSeed: readonly number[];
}

/**
 * A liberty a grid PRODUCER took to make a usable grid — named, never silent
 * (LAWS). Structurally identical to the analyzer's EvidenceMarker so the
 * analysis folds grid repairs straight into its evidence stream.
 */
export interface GridRepair {
  readonly code: string;
  readonly detail?: Readonly<Record<string, number | string | boolean | readonly number[]>>;
}

/**
 * The SHARED voxel substrate (ADR-021 §4): an occupancy volume with an optional
 * world embedding, and nothing hand-shaped about it. Two consumers today — the
 * hand analyzer (via {@link VoxelGrid}, which adds the wrist seed) and the
 * chest-integrity weight repair (weights/bound-influence), which measures
 * geodesic bone support through this volume and needs no seed at all.
 */
export interface VolumeGrid {
  readonly dims: readonly [number, number, number];
  /** Geodesic distance unit h (each 6-connected step costs one h). */
  readonly voxelSize: number;
  /** Occupied voxels as linear indices `x + nx*(y + ny*z)`. */
  readonly occupied: ReadonlySet<number>;
  /**
   * World-space position of voxel coordinate (0,0,0)'s LOWER CORNER — i.e. the
   * grid's world origin. When present, the grid carries a full world embedding:
   * the world position of a (possibly fractional) voxel point `p` is, per axis,
   * `origin[a] + (p[a] + 0.5) · voxelSize` (the VOXEL-CENTER convention the
   * voxelizer marks against). See {@link voxelPointToWorld}.
   *
   * ONLY grids built from a real mesh (`voxelizeVolume`/`voxelizeMesh`) carry
   * this — the analyzer (E1) works purely in voxel-index space and never reads
   * it; the declarative box-fixtures (`gridFromBoxes`) have NO world embedding
   * and leave it absent. The graft (E3c) and the weight repair need it to relate
   * voxels to bind-world; asking for it on a grid without an origin is a wiring
   * bug, caught fail-loud at the seam.
   */
  readonly origin?: readonly [number, number, number];
  /**
   * Liberties the producer took (e.g. the wrist band re-banded onto the main
   * voxel component when the declared cut seeded nothing). The analyzer folds
   * these into its evidence markers; absent means none were needed.
   */
  readonly repairs?: readonly GridRepair[];
}

/** The hand analyzer's input: the shared volume substrate plus the wrist seed. */
export interface VoxelGrid extends VolumeGrid {
  /** Wrist-cut voxels — the BFS distance-0 shell — as linear indices. */
  readonly wristSeed: ReadonlySet<number>;
}

/** Structural violation of the VoxelGrid contract — a broken producer, fail-loud (ADR-010 §7). */
export class InvalidVoxelGridError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(
      `Voxel grid is structurally invalid (${issues.length} issue(s)):\n` +
        issues.map((i) => `  - ${i}`).join('\n'),
    );
  }
}

/** Linear index for a voxel coordinate under the `x + nx*(y + ny*z)` convention. */
export function voxelIndex(x: number, y: number, z: number, dims: readonly [number, number, number]): number {
  return x + dims[0] * (y + dims[1] * z);
}

/** Inverse of {@link voxelIndex}: recover integer voxel coordinates from a linear index. */
export function voxelCoords(i: number, dims: readonly [number, number, number]): readonly [number, number, number] {
  const nx = dims[0];
  const ny = dims[1];
  return [i % nx, Math.floor(i / nx) % ny, Math.floor(i / (nx * ny))];
}

/** A voxel→world transform was requested on a grid that carries no world embedding. */
export class NoWorldEmbeddingError extends KernelError {
  constructor() {
    super(
      'VoxelGrid has no `origin` — it was not built from a real mesh (voxelizeMesh), ' +
        'so it carries no world embedding. Box-fixture grids are voxel-index only; ' +
        'a voxel→world transform on one is a wiring bug (ADR-010 §7).',
    );
  }
}

/**
 * World-space position of a (possibly fractional) voxel point under the grid's
 * VOXEL-CENTER convention: `origin[a] + (p[a] + 0.5) · voxelSize` per axis. This
 * is the exact inverse of the voxelizer's own `voxelCoords`/`worldPos` — the
 * transform is a uniform scale + translation (no rotation), which is precisely
 * why a voxel-space DIRECTION doubles as a world direction (the E3b frames): the
 * translation cancels in a difference and the uniform scale drops out on
 * normalization. Fail-loud {@link NoWorldEmbeddingError} when the grid has no
 * origin — never a silent (0,0,0)-anchored guess.
 */
export function voxelPointToWorld(grid: VolumeGrid, p: VoxelPoint): Vec3Data {
  if (grid.origin === undefined) throw new NoWorldEmbeddingError();
  const [ox, oy, oz] = grid.origin;
  const h = grid.voxelSize;
  return { x: ox + (p.x + 0.5) * h, y: oy + (p.y + 0.5) * h, z: oz + (p.z + 0.5) * h };
}

/**
 * The exact mirror of {@link voxelPointToWorld}: a world position as a
 * (fractional, unclamped) voxel point. `p[a] = (world[a] − origin[a]) / voxelSize − 0.5`.
 *
 * ADR-022 §5.1 makes this pair the ONE owner of the world↔voxel conversion. A
 * digit pin persists in bind-world (pose- and resolution-independent) while
 * placement is grid arithmetic, so every graft crosses this seam — and a
 * hand-rolled copy of the arithmetic at a call site is exactly the drifting
 * seam the authoring policy names. Same fail-loud contract as its mirror.
 *
 * FRACTIONAL AND UNCLAMPED BY DESIGN: a pin may legitimately sit slightly
 * outside the grid (a tip on the surface of a volume whose bounding box was
 * cropped), and rounding here would quietly move a person's answer. Callers
 * that need an occupied voxel must ask for one explicitly.
 */
export function worldPointToVoxel(grid: VolumeGrid, world: Vec3Data): VoxelPoint {
  if (grid.origin === undefined) throw new NoWorldEmbeddingError();
  const [ox, oy, oz] = grid.origin;
  const h = grid.voxelSize;
  return { x: (world.x - ox) / h - 0.5, y: (world.y - oy) / h - 0.5, z: (world.z - oz) / h - 0.5 };
}

/**
 * Distance from a (fractional) voxel point to the nearest OCCUPIED voxel centre,
 * in voxel units — the "is this point on the thing?" question, answered as a
 * number rather than a boolean so the caller can name it in a refusal.
 *
 * WHY A DISTANCE AND NOT A MEMBERSHIP TEST. A tip is a point on the SURFACE, and
 * the surface is exactly where quantization is worst: a legitimate fingertip
 * routinely lands a fraction of a voxel outside the last occupied cell, so
 * `occupied.has(round(p))` would reject good pins. A distance lets the caller
 * separate "on the skin, quantized" from "somewhere else entirely" — and the
 * second case is what {@link worldPointToVoxel}'s deliberate lack of clamping
 * would otherwise let through as a giant extrapolated finger (Codex P2, #92).
 *
 * Returns Infinity for an empty grid. O(occupied) per call.
 */
export function distanceToOccupied(grid: VolumeGrid, p: VoxelPoint): number {
  const [nx, ny] = [grid.dims[0], grid.dims[1]];
  let best = Infinity;
  for (const i of grid.occupied) {
    const x = i % nx;
    const y = Math.floor(i / nx) % ny;
    const z = Math.floor(i / (nx * ny));
    const d = (x - p.x) ** 2 + (y - p.y) ** 2 + (z - p.z) ** 2;
    if (d < best) best = d;
  }
  return Math.sqrt(best);
}

/**
 * Centroid of the wrist-seed voxels in VOXEL-INDEX space — the wrist cut plane's
 * centre, i.e. the true middle of the wrist. This is the origin of the E3a
 * finger construction (place-digits lays joints along wrist→tip): the host hand
 * BONE is not usable for this, because on some generated rigs it sits forward of
 * the palm (measured variance — HAND_STUDIO_KNUCKLES.md), while the seed voxels
 * are where the hand was actually severed. Fail-loud on an empty seed — a grid
 * like that is structurally invalid anyway ({@link validateVoxelGrid}).
 */
export function wristSeedCentroid(grid: VoxelGrid): VoxelPoint {
  let sx = 0;
  let sy = 0;
  let sz = 0;
  let n = 0;
  for (const i of grid.wristSeed) {
    const [x, y, z] = voxelCoords(i, grid.dims);
    sx += x;
    sy += y;
    sz += z;
    n += 1;
  }
  if (n === 0) {
    throw new InvalidVoxelGridError(['wristSeed is empty — no wrist centroid to place digits from']);
  }
  return { x: sx / n, y: sy / n, z: sz / n };
}

/**
 * The ROBUST wrist-cut centre (foxDude field defect, 2026-08-02): the slab of
 * solid immediately at an open cut is exactly where the voxel fill is least
 * trustworthy — a ragged shell opening leaks cells, and the one-voxel seed
 * band can be a tiny scrap sitting OFF the flesh (measured: R_Hand seed of 8
 * cells centred 2.6 cm behind the wrist tube while L_Hand's 14 sat true —
 * and `wristSeedCentroid` of that scrap moved the recentred pivot to the
 * opposite edge). This walks slabs DISTALLY from the plane, skips the ragged
 * opening by requiring each slab's mass to be comparable to its distal
 * neighbor (the cross-section has stabilized), and returns that stable
 * slab's transverse centroid PROJECTED BACK ONTO THE PLANE — the tube's
 * middle at the cut, measured where the fill can be believed.
 *
 * `wristSeed` itself is untouched: the analyzer roots its geodesics there
 * and its semantics are "where the hand was severed", not "the middle".
 * Consumers that need a GEOMETRIC middle (the wrist recenter, digit
 * placement's wrist origin) use this. Fail-loud on a grid with no occupied
 * slab in range — that grid was structurally invalid anyway.
 */
export interface CutCentreResult {
  /** Voxel-index-space centre, on the cut plane (same convention as wristSeedCentroid). */
  readonly centre: VoxelPoint;
  /** Which slab (0 = at the plane) supplied the cross-section — evidence, surfaceable. */
  readonly slabUsed: number;
  /** Occupied-cell count of the slab used. */
  readonly slabMass: number;
  /** Distance (voxels) between this centre and the raw seed centroid — the correction's size. */
  readonly seedDisagreement: number;
}

/** A slab's mass must reach this fraction of its distal neighbor's to be trusted. */
const STABLE_SLAB_FRACTION = 0.6;
const MAX_SLAB_WALK = 8;

export function robustCutCentre(
  grid: VoxelGrid,
  plane: { readonly point: readonly [number, number, number]; readonly normal: readonly [number, number, number] },
): CutCentreResult {
  if (grid.origin === undefined) throw new NoWorldEmbeddingError();
  const [ox, oy, oz] = grid.origin;
  const h = grid.voxelSize;
  // The plane in voxel-index space: uniform scale + translation, so the
  // normal direction carries over unchanged (see voxelPointToWorld's note).
  const nLen = Math.hypot(plane.normal[0], plane.normal[1], plane.normal[2]);
  const n = { x: plane.normal[0] / nLen, y: plane.normal[1] / nLen, z: plane.normal[2] / nLen };
  const p0 = { x: (plane.point[0] - ox) / h - 0.5, y: (plane.point[1] - oy) / h - 0.5, z: (plane.point[2] - oz) / h - 0.5 };

  interface Slab {
    n: number;
    sx: number;
    sy: number;
    sz: number;
  }
  const slabs: Slab[] = Array.from({ length: MAX_SLAB_WALK }, () => ({ n: 0, sx: 0, sy: 0, sz: 0 }));
  for (const i of grid.occupied) {
    const [x, y, z] = voxelCoords(i, grid.dims);
    const d = (x - p0.x) * n.x + (y - p0.y) * n.y + (z - p0.z) * n.z;
    if (d < 0 || d >= MAX_SLAB_WALK) continue;
    const slab = slabs[Math.floor(d)]!;
    slab.n += 1;
    slab.sx += x;
    slab.sy += y;
    slab.sz += z;
  }
  // First slab whose mass is comparable to its distal neighbor — past the
  // ragged opening, before the palm broadens the reading pointlessly.
  let k = -1;
  for (let i = 0; i < MAX_SLAB_WALK - 1; i += 1) {
    const here = slabs[i]!.n;
    const next = slabs[i + 1]!.n;
    if (here > 0 && next > 0 && here >= STABLE_SLAB_FRACTION * next) {
      k = i;
      break;
    }
  }
  if (k === -1) {
    // No stable pair — fall back to the fattest slab in range, loudly absent
    // only when NOTHING is occupied near the plane.
    let best = 0;
    for (let i = 1; i < MAX_SLAB_WALK; i += 1) if (slabs[i]!.n > slabs[best]!.n) best = i;
    if (slabs[best]!.n === 0) {
      throw new InvalidVoxelGridError(['no occupied slab within range of the cut plane — no wrist centre to measure']);
    }
    k = best;
  }
  const s = slabs[k]!;
  const centroid = { x: s.sx / s.n, y: s.sy / s.n, z: s.sz / s.n };
  // Project the stable cross-section's centroid back ONTO the plane: the
  // transverse middle at the cut, without the slab's distal offset.
  const dist = (centroid.x - p0.x) * n.x + (centroid.y - p0.y) * n.y + (centroid.z - p0.z) * n.z;
  const centre = { x: centroid.x - dist * n.x, y: centroid.y - dist * n.y, z: centroid.z - dist * n.z };
  const seed = wristSeedCentroid(grid);
  const seedDisagreement = Math.hypot(centre.x - seed.x, centre.y - seed.y, centre.z - seed.z);
  return { centre, slabUsed: k, slabMass: s.n, seedDisagreement };
}

function inBounds(x: number, y: number, z: number, dims: readonly [number, number, number]): boolean {
  return x >= 0 && y >= 0 && z >= 0 && x < dims[0] && y < dims[1] && z < dims[2];
}

/**
 * Enumerate the occupied indices of a union of inclusive integer boxes, once
 * each, in ascending index order (treatment §7 — complete + deterministic).
 * Out-of-range box cells are clipped, matching the reference's `expandBoxes`.
 */
export function expandBoxes(
  dims: readonly [number, number, number],
  boxes: readonly VoxelBox[],
): number[] {
  const set = new Set<number>();
  for (const b of boxes) {
    for (let z = b.min[2]; z <= b.max[2]; z += 1) {
      for (let y = b.min[1]; y <= b.max[1]; y += 1) {
        for (let x = b.min[0]; x <= b.max[0]; x += 1) {
          if (inBounds(x, y, z, dims)) set.add(voxelIndex(x, y, z, dims));
        }
      }
    }
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Build a {@link VoxelGrid} from the declarative box-fixture form. Pure
 * construction — it does NOT validate (the analyzer owns the fail-loud
 * boundary), so a fixture can deliberately encode a seed off the volume for the
 * `invalid-grid` path. `occupied` wins over `shapes` when both are present (the
 * reference's precedence); voxelSize defaults to 1.
 */
export function gridFromBoxes(fixture: BoxFixture): VoxelGrid {
  const occupied =
    fixture.occupied !== undefined
      ? new Set<number>(fixture.occupied)
      : new Set<number>(expandBoxes(fixture.dims, fixture.shapes ?? []));
  return {
    dims: fixture.dims,
    voxelSize: fixture.voxelSize ?? 1,
    occupied,
    wristSeed: new Set<number>(fixture.wristSeed),
  };
}

/**
 * Throw {@link InvalidVoxelGridError} on any STRUCTURAL contract violation (see
 * the module header for the exact line drawn between a thrown error and the
 * analyzer's named `invalid-grid` result). Reports every problem found, not
 * just the first — the OntologyIntegrityError/SchemaValidationError pattern.
 */
export function validateVoxelGrid(grid: VoxelGrid): void {
  const issues = volumeGridIssues(grid);
  const { dims, wristSeed } = grid;
  const dimsOk = dims.length === 3 && dims.every((d) => Number.isInteger(d) && d > 0);
  if (wristSeed.size === 0) issues.push('wristSeed is empty — analysis needs a wrist cut to seed the field');
  if (dimsOk) {
    const total = dims[0]! * dims[1]! * dims[2]!;
    for (const i of wristSeed) {
      if (!Number.isInteger(i) || i < 0 || i >= total) {
        issues.push(`wristSeed index ${i} is out of range [0, ${total})`);
      }
    }
  }
  if (issues.length > 0) throw new InvalidVoxelGridError(issues);
}

/**
 * The seed-free structural gate for the shared substrate: everything
 * {@link validateVoxelGrid} checks except the wrist seed, which only the hand
 * analyzer requires. Throws {@link InvalidVoxelGridError} with every issue.
 */
export function validateVolumeGrid(grid: VolumeGrid): void {
  const issues = volumeGridIssues(grid);
  if (issues.length > 0) throw new InvalidVoxelGridError(issues);
}

function volumeGridIssues(grid: VolumeGrid): string[] {
  const issues: string[] = [];
  const { dims, voxelSize, occupied } = grid;
  if (dims.length !== 3) issues.push(`dims must have length 3, got ${dims.length}`);
  for (let axis = 0; axis < dims.length; axis += 1) {
    const d = dims[axis]!;
    if (!Number.isInteger(d) || d <= 0) issues.push(`dims[${axis}] must be a positive integer, got ${d}`);
  }
  if (!Number.isFinite(voxelSize) || voxelSize <= 0) {
    issues.push(`voxelSize must be a finite positive number, got ${voxelSize}`);
  }
  // The world embedding is optional (box-fixtures have none), but a present one
  // must be well-formed — a broken origin misplaces every consumer's world read.
  if (grid.origin !== undefined) {
    if (grid.origin.length !== 3) {
      issues.push(`origin must have length 3, got ${grid.origin.length}`);
    }
    grid.origin.forEach((c, a) => {
      if (!Number.isFinite(c)) issues.push(`origin[${a}] must be finite, got ${c}`);
    });
  }
  // Only bounds-check indices once dims are known-good, else `total` is garbage.
  const dimsOk = dims.length === 3 && dims.every((d) => Number.isInteger(d) && d > 0);
  if (occupied.size === 0) issues.push('occupied is empty — a grid must carry volume');
  if (dimsOk) {
    const total = dims[0]! * dims[1]! * dims[2]!;
    for (const i of occupied) {
      if (!Number.isInteger(i) || i < 0 || i >= total) {
        issues.push(`occupied index ${i} is out of range [0, ${total})`);
      }
    }
  }
  return issues;
}
